<script setup lang="ts">
import { Bot, Brain, FolderKanban, ShieldCheck } from 'lucide-vue-next'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useAgentStore, useProjectStore } from '@/stores/domain'

const agentStore = useAgentStore()
const projectStore = useProjectStore()
const { agents } = storeToRefs(agentStore)
const active = computed(() => agents.value.filter((agent) => agent.status === 'active'))
const projectName = (projectId?: string) =>
  projectStore.projects.find((project) => project.id === projectId)?.name ?? 'Shared specialist'
</script>

<template>
  <div class="section-page">
    <header class="section-page__header">
      <div><span class="eyebrow">Agent factory</span><h1>Agent teams</h1><p>Manager-governed specialists, scoped permissions, memory, and project assignments.</p></div>
    </header>
    <div class="metric-strip">
      <div><span>Active agents</span><strong>{{ active.length }}</strong></div>
      <div><span>Total agents</span><strong>{{ agents.length }}</strong></div>
      <div><span>Projects staffed</span><strong>{{ new Set(active.map((agent) => agent.projectId).filter(Boolean)).size }}</strong></div>
      <div><span>Creation authority</span><strong><small>Manager only</small></strong></div>
    </div>
    <section class="detail-card detail-card--wide">
      <div class="detail-card__head"><div><span class="eyebrow">Directory</span><h2>Logical workers</h2></div><Bot :size="18" /></div>
      <div v-if="agents.length" class="team-list">
        <div v-for="agent in agents" :key="agent.id">
          <i class="specialist-avatar specialist-avatar--purple"><Bot :size="14" /></i>
          <span><strong>{{ agent.name }}</strong><small>{{ agent.role }} · {{ projectName(agent.projectId) }} · {{ agent.status }}</small></span>
        </div>
      </div>
      <p v-else>Ask your Manager to build a project team. Suitable active specialists will be reused before new ones are created.</p>
    </section>
    <div class="detail-grid">
      <section class="detail-card"><div class="detail-card__head"><div><span class="eyebrow">Access</span><h2>Explicit grants</h2></div><ShieldCheck :size="18" /></div><p>Each worker receives only its declared permissions and credential access.</p></section>
      <section class="detail-card"><div class="detail-card__head"><div><span class="eyebrow">Context</span><h2>Scoped memory</h2></div><Brain :size="18" /></div><p>Memory and RAG namespaces are attached to the logical agent record.</p></section>
      <section class="detail-card"><div class="detail-card__head"><div><span class="eyebrow">Ownership</span><h2>Project teams</h2></div><FolderKanban :size="18" /></div><p>Workers execute tasks independently and report results back to the Manager.</p></section>
    </div>
  </div>
</template>
